import React from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'
import { When } from 'react-if'
import { Button } from './Button'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'

export enum ModalAction {
  ACCEPT = 'ACCEPT',
  CANCEL = 'CANCEL',
  DECLINE = 'DECLINE',
}

export interface AppModalProps {
  open: boolean
  setOpen: (isOpen: boolean) => void
  title?: string
  closeable?: boolean
  className?: string
  actions?: Partial<
    Record<
      ModalAction,
      {
        caption?: string
        onClick: () => void
      }
    >
  >
}

const modalButtonClasses: Record<ModalAction, string> = {
  [ModalAction.ACCEPT]: 'justify-self-end bg-glow text-black',
  [ModalAction.CANCEL]: 'justify-self-end bg-primary',
  [ModalAction.DECLINE]: 'justify-self-start bg-red text-white',
}

export const Modal: React.FC<AppModalProps & React.PropsWithChildren> = ({
  open,
  setOpen,
  title,
  closeable,
  actions,
  children,
  className,
}) => {
  const t = useTranslations('modal')

  return (
    <Dialog modal open={open} onOpenChange={setOpen}>
      <DialogContent
        aria-describedby="modal"
        className={cn('sm:max-w-[425px]', className)}
      >
        <DialogHeader>
          <When condition={!!title}>
            <DialogTitle>{title}</DialogTitle>
            <When condition={closeable}>
              <DialogClose />
            </When>
          </When>
        </DialogHeader>
        {children}
        <When condition={!!actions}>
          <DialogFooter className="flex w-full">
            {Object.entries(actions || {})?.map(([key, value]) => (
              <Button
                key={key}
                onClick={value.onClick}
                className={modalButtonClasses[key as ModalAction]}
              >
                {value.caption ?? t(`actions.${key}`)}
              </Button>
            ))}
          </DialogFooter>
        </When>
      </DialogContent>
    </Dialog>
  )
}
